/**
 * Validación de documentos de identidad españoles.
 *
 * Vive en `@naar/shared` porque se usa en los DOS lados: el resolver del formulario en la web
 * y la validación del cuerpo en la API. Escribirla dos veces garantiza que un día divergan.
 *
 * El dígito de control es determinista, así que esto no es una heurística: un DNI con letra
 * incorrecta es inválido con certeza, y podemos decírselo al usuario en el propio campo.
 */

/** Tabla oficial del dígito de control. El índice es `número % 23`. */
const CONTROL_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

/** Prefijo del NIE → dígito por el que se sustituye antes de calcular el módulo. */
const NIE_PREFIX: Record<string, string> = { X: '0', Y: '1', Z: '2' };

const DNI_PATTERN = /^(\d{8})([A-Z])$/;
const NIE_PATTERN = /^([XYZ])(\d{7})([A-Z])$/;
/**
 * Pasaporte: no tiene dígito de control, así que solo se puede validar forma. Se admite el
 * formato español (3 letras/dígitos + 6 dígitos) y, de forma laxa, cualquier alfanumérico de
 * 6 a 12 caracteres, porque un inquilino extranjero trae el pasaporte de su país.
 */
const PASSPORT_PATTERN = /^[A-Z0-9]{6,12}$/;

export type IdDocumentKind = 'dni' | 'nie' | 'pasaporte';

/**
 * Normaliza antes de comprobar: mayúsculas, sin espacios ni guiones.
 * Es el valor que hay que GUARDAR, para que el único `(organization_id, id_document)` detecte
 * que `12345678-Z` y `12345678z` son la misma persona.
 */
export function normalizeIdDocument(value: string): string {
  return value.toUpperCase().replace(/[\s\-.]/g, '');
}

function controlLetterOf(numericPart: string): string {
  return CONTROL_LETTERS[Number(numericPart) % 23]!;
}

export function isValidDni(value: string): boolean {
  const match = DNI_PATTERN.exec(normalizeIdDocument(value));
  if (!match) return false;
  return controlLetterOf(match[1]!) === match[2];
}

export function isValidNie(value: string): boolean {
  const match = NIE_PATTERN.exec(normalizeIdDocument(value));
  if (!match) return false;
  return controlLetterOf(`${NIE_PREFIX[match[1]!]}${match[2]}`) === match[3];
}

export function isValidPassport(value: string): boolean {
  return PASSPORT_PATTERN.test(normalizeIdDocument(value));
}

export function isValidIdDocument(type: IdDocumentKind, value: string): boolean {
  switch (type) {
    case 'dni':
      return isValidDni(value);
    case 'nie':
      return isValidNie(value);
    case 'pasaporte':
      return isValidPassport(value);
  }
}

/**
 * Mensaje de error para el campo, en español y explicando QUÉ falla. «Documento inválido» no
 * ayuda a nadie; «la letra de control debería ser Z» se corrige en dos segundos.
 */
export function idDocumentError(type: IdDocumentKind, value: string): string | null {
  const normalized = normalizeIdDocument(value);
  if (normalized === '') return 'Introduce el número de documento.';

  if (type === 'dni') {
    const match = DNI_PATTERN.exec(normalized);
    if (!match) return 'Un DNI son 8 dígitos seguidos de una letra. Ej.: 12345678Z';
    const expected = controlLetterOf(match[1]!);
    return expected === match[2] ? null : `La letra de control debería ser ${expected}.`;
  }

  if (type === 'nie') {
    const match = NIE_PATTERN.exec(normalized);
    if (!match) return 'Un NIE es X, Y o Z, 7 dígitos y una letra. Ej.: X1234567L';
    const expected = controlLetterOf(`${NIE_PREFIX[match[1]!]}${match[2]}`);
    return expected === match[3] ? null : `La letra de control debería ser ${expected}.`;
  }

  return isValidPassport(normalized)
    ? null
    : 'Un pasaporte son entre 6 y 12 caracteres alfanuméricos.';
}

/**
 * Partículas de los nombres españoles que NO cuentan como palabra para las iniciales.
 * Sin esta lista, «María del Carmen Ruiz» daría «MD» en lugar de «MR».
 */
const NAME_PARTICLES = new Set([
  'de',
  'del',
  'la',
  'las',
  'lo',
  'los',
  'y',
  'e',
  'da',
  'das',
  'do',
  'dos',
  'van',
  'von',
  'di',
  "d'",
]);

/**
 * Iniciales derivadas del nombre. NO es un campo del formulario: se calcula.
 *
 * Toma la primera palabra (nombre de pila) y la primera palabra que parezca apellido, es
 * decir, la siguiente que no sea una partícula. `avatar_initials` es `VARCHAR(4)` y aquí
 * siempre se devuelven 1 o 2 caracteres.
 *
 * LÍMITE CONOCIDO: con nombre compuesto, «María del Carmen Ruiz» da «MC» y no «MR». No hay
 * forma determinista de saber que «Carmen» pertenece al nombre de pila y no es el primer
 * apellido; haría falta un diccionario de nombres, que fallaría con cualquier nombre
 * extranjero. Se prefiere una regla predecible y documentada a una heurística que acierta
 * en español y se equivoca en todo lo demás.
 */
export function initialsOf(fullName: string): string {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';

  const first = words[0]!;
  const surname = words.slice(1).find((word) => !NAME_PARTICLES.has(word.toLowerCase()));

  if (!surname) return first.slice(0, 2).toUpperCase();
  return `${first[0]}${surname[0]}`.toUpperCase();
}
